import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Fragment, FunctionComponent, ReactNode } from 'react';

import Chevrelon from '../../icons/cheverlon';
import { useAppContext } from '../../../providers/app-context';

const Select: FunctionComponent<{
  menuPlacement?: 'top' | 'bottom';
  disabled?: boolean;
  element?: ReactNode;
  onChange: (value: any) => void;
  label: ReactNode;
  value: any;
  options?: Array<{ id: string; value: any; element: ReactNode }>;
  hasError?: boolean;
  icon?: ReactNode;
  id?: string;
  dataCy?: string;
  isBparts?: boolean;
  border?: boolean;
  minHeight?: boolean;
  checkoutSelect?: boolean;
  /**
   * Liga o modo controlado do `Listbox`. Por omissão fica não-controlado, como
   * desde o "Shipping location drawer" (2024): os seletores de localização
   * dependem de reagir ao clique antes de o servidor confirmar.
   */
  controlled?: boolean;
}> = ({
  value,
  options,
  label,
  onChange,
  element,
  disabled,
  menuPlacement = 'bottom',
  hasError,
  icon,
  id,
  dataCy,
  isBparts = false,
  border = true,
  minHeight = true,
  checkoutSelect = false,
  controlled = false,
}) => (
  <Listbox
    {...(controlled ? { value } : { defaultValue: value })}
    onChange={onChange}
    disabled={disabled}
    as={'section'}
    className={classNames(
      'relative',
      checkoutSelect
        ? 'border border-neutral-30'
        : border && 'border-b border-neutral-30',
      'max-w-[100vw] lg:max-w-none',
      hasError ? 'border-red-100 text-red-100' : 'border-neutral-30'
    )}
    id={id}
    data-cy={dataCy}
  >
    <div className="group">
      <Listbox.Button
        className={classNames(
          'w-full flex relative px-4 box-border gap-4',
          hasError ? 'group-hover:bg-red-10' : 'group-hover:bg-brand-10',
          checkoutSelect && !value
            ? 'py-2 min-h-12 items-center'
            : checkoutSelect && value
            ? 'py-2'
            : minHeight
            ? 'min-h-[3rem] py-0.5'
            : 'py-2',
          label ? 'items-end' : 'items-center'
        )}
      >
        <div
          className={classNames(
            'w-full min-w-0 cursor-pointer bg-white disabled:bg-neutral-30 disabled:opacity-90 text-left focus:outline-none focus:bg-brand-10 focus:border-brand-20 sm:text-sm'
          )}
        >
          {label && (
            <span
              className={classNames(
                checkoutSelect && !value
                  ? 'text-base'
                  : 'absolute block text-base tracking-normal transition-transform duration-300 origin-top-left',
                !hasError && (isBparts ? 'text-blue-100' : 'text-brand-100'),
                hasError ? 'group-hover:bg-red-10' : 'group-hover:bg-brand-10',
                !!value
                  ? 'left-4 scale-[0.9] -translate-y-full'
                  : 'left-4 top-1/2 scale-100 -translate-y-1/2'
              )}
            >
              {label}
            </span>
          )}
          <span
            className={classNames(
              'block tracking-normal transition-transform text-base delay-300 duration-300 group-hover:bg-brand-10 truncate',
              !!value ? 'opacity-100' : 'opacity-0'
            )}
          >
            {element}
          </span>
        </div>
        {icon && <span className="my-auto w-6 ">{icon}</span>}
        <span className="my-auto pointer-events-none">
          <Chevrelon className="w-4 text-black" />
        </span>
      </Listbox.Button>
    </div>
    <Transition
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Listbox.Options
        className={classNames(
          'bg-white z-10 absolute max-h-60 w-full overflow-auto py-1 text-base shadow-sm focus:outline-none sm:text-sm border border-neutral-30',
          menuPlacement === 'top'
            ? '-translate-y-full top-[-1px]'
            : 'bottom-[1px] translate-y-full'
        )}
      >
        {options?.map((option) => (
          <Listbox.Option
            key={option.id}
            className={({ active, selected }) =>
              classNames(
                `relative items-center cursor-default select-none min-h-[3rem] grid text-sm px-4 focus-visible:border-brand-10`,
                selected ? 'bg-brand-20 text-neutral-80' : 'text-neutral-70',
                active ? 'bg-brand-10 text-neutral-80' : 'text-neutral-70'
              )
            }
            value={option.value}
          >
            {option.element}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Transition>
  </Listbox>
);

export default Select;
